import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import logger from '../lib/logger';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { appointmentsAPI, barbersAPI, servicesAPI, clientsAPI } from '../lib/api';
import { formatDate, formatCurrency, cn, getErrorMessage, cleanPayload } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import SearchableSelect from '../components/SearchableSelect';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Calendar } from '../components/ui/calendar';
import { History as HistoryIcon, Globe } from 'lucide-react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon,
  Plus,
  Clock,
  User,
  Phone,
  Scissors,
  MoreVertical,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  LogIn,
  Loader2,
  GripVertical,
  Move,
  Palette,
  Tag,
  CreditCard,
  X,
  FileText,
  Trash2,
  Edit,
  ArrowLeft,
  Lock,
  Gift,
} from 'lucide-react';
import { toast } from 'sonner';

// Time slots every 15 minutes within a given range
const generateTimeSlots = (startHour = 9, endHour = 20) => {
  const slots = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    for (let min = 0; min < 60; min += 15) {
      if (hour === endHour && min > 0) break;
      slots.push({
        hour,
        minute: min,
        label: `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`,
        isHourStart: min === 0
      });
    }
  }
  return slots;
};

const SLOT_HEIGHT = 28; // pixels per 15-minute slot
const DRAG_THRESHOLD = 150; // ms to distinguish click vs drag

// Status colors and labels
const STATUS_CONFIG = {
  pending: { 
    label: 'Creada', 
    bgColor: '#a8d5a2',
    borderColor: '#6bbf59',
    textColor: '#2d5a26'
  },
  confirmed: { 
    label: 'Confirmada', 
    bgColor: '#4a8c3f',
    borderColor: '#2d5a26',
    textColor: '#ffffff'
  },
  arrived: { 
    label: 'Llegada', 
    bgColor: '#4a90d9',
    borderColor: '#2563eb',
    textColor: '#ffffff'
  },
  cancelled: { 
    label: 'Cancelada', 
    bgColor: '#fecaca',
    borderColor: '#ef4444',
    textColor: '#991b1b'
  },
  admin: { 
    label: 'Administrativa', 
    bgColor: '#fcd34d',
    borderColor: '#b8860b',
    textColor: '#78350f'
  },
  completed: { 
    label: 'Completada', 
    bgColor: '#d1d5db',
    borderColor: '#6b7280',
    textColor: '#374151'
  }
};

// Preset colors for custom color picker
const PRESET_COLORS = [
  '#a8d5a2', '#4a8c3f', '#4a90d9', '#fecaca', '#fcd34d', '#d1d5db',
  '#f472b6', '#c084fc', '#fb923c', '#22d3ee', '#84cc16', '#f43f5e'
];

// Common tags
const COMMON_TAGS = [
  'VIP', 'Primera vez', 'Puntual', 'Frecuente', 'Cumpleaños', 'Promoción',
  'Urgente', 'Cortesía', 'Prepagado', 'Seguimiento'
];

// Get current date in CDMX timezone as YYYY-MM-DD
const getCDMXDate = () => {
  // en-CA yields ISO-like YYYY-MM-DD; with explicit timezone it's robust to browser TZ
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });
};

// Get business open/close hours for a given date string (YYYY-MM-DD)
const getBusinessHoursForDate = (business, dateStr) => {
  const fallback = { startHour: 9, endHour: 20 };
  if (!business?.schedule) return fallback;
  // Use CDMX timezone to derive day of week consistently
  const date = new Date(dateStr + 'T12:00:00');
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayKey = days[date.getDay()];
  const day = business.schedule[dayKey];
  if (!day || day.open === 'closed' || !day.open) return fallback;
  const [openH] = (day.open || '09:00').split(':').map(Number);
  const [closeH, closeM] = (day.close || '20:00').split(':').map(Number);
  // Round close up to whole hour for display purposes
  const endHour = closeM > 0 ? Math.min(closeH + 1, 23) : closeH;
  return {
    startHour: Number.isFinite(openH) ? openH : 9,
    endHour: Number.isFinite(endHour) && endHour > openH ? endHour : 20
  };
};

export default function AgendaGridPage({ privateMode = false }) {
  const { selectedBusiness, isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(getCDMXDate());
  const [onlyActive, setOnlyActive] = useState(() => {
    try {
      const v = sessionStorage.getItem('agenda:onlyActive');
      return v === null ? true : v === 'true';
    } catch (_) {
      return true;
    }
  });
  useEffect(() => {
    try { sessionStorage.setItem('agenda:onlyActive', String(onlyActive)); } catch (_) { /* ignore */ }
  }, [onlyActive]);
  // Barbería / Salón / Todos filter for employee columns
  const [staffType, setStaffType] = useState(() => {
    try { return sessionStorage.getItem('agenda:staffType') || 'all'; } catch (_) { return 'all'; }
  });
  useEffect(() => {
    try { sessionStorage.setItem('agenda:staffType', staffType); } catch (_) { /* ignore */ }
  }, [staffType]);

  const [appointments, setAppointments] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasPrivateBarbers, setHasPrivateBarbers] = useState(false);

  // Only master admin may enter private mode
  useEffect(() => {
    if (privateMode && !user?.is_master_admin) {
      navigate('/agenda', { replace: true });
    }
  }, [privateMode, user, navigate]);

  // Check whether any private barbers exist (only master admin needs to know)
  useEffect(() => {
    if (!isAdmin() || !selectedBusiness) return;
    barbersAPI.getAll({ only_private: true })
      .then((res) => setHasPrivateBarbers((res.data || []).length > 0))
      .catch(() => setHasPrivateBarbers(false));
  }, [isAdmin, selectedBusiness]);

  // Build time slots based on the union of the business schedule and every
  // barber's personal schedule for the selected date. This ensures the grid
  // shows enough rows to honour barbers that open earlier or close later than
  // the business. Falls back to 09:00-20:00 if everything is missing.
  const timeSlots = useMemo(() => {
    let { startHour, endHour } = getBusinessHoursForDate(selectedBusiness, currentDate);

    const date = new Date(currentDate + 'T12:00:00');
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayKey = days[date.getDay()];

    for (const b of (barbers || [])) {
      const cfg = b.work_schedule?.[dayKey];
      if (!cfg?.is_working) continue;
      if (cfg.start_time) {
        const [h] = cfg.start_time.split(':').map(Number);
        if (Number.isFinite(h)) startHour = Math.min(startHour, h);
      }
      if (cfg.end_time) {
        const [h, m] = cfg.end_time.split(':').map(Number);
        const rounded = (m && m > 0) ? Math.min(h + 1, 23) : h;
        if (Number.isFinite(rounded)) endHour = Math.max(endHour, rounded);
      }
    }

    return generateTimeSlots(startHour, endHour);
  }, [selectedBusiness, currentDate, barbers]);
  
  // Dialog states
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [appointmentTab, setAppointmentTab] = useState('detalles');
  const [appointmentHistory, setAppointmentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  // Drag state - only activates after holding for DRAG_THRESHOLD ms
  const [draggingAppointment, setDraggingAppointment] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const dragTimerRef = useRef(null);
  const mouseDownTimeRef = useRef(0);
  const mouseDownPosRef = useRef({ x: 0, y: 0 });
  
  // Resize state
  const [resizingAppointment, setResizingAppointment] = useState(null);
  const [resizeStartY, setResizeStartY] = useState(0);
  
  // Quick actions popover
  const [quickActionsAppointment, setQuickActionsAppointment] = useState(null);

  // Inline "Nuevo cliente" from the appointment dialog
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newClientData, setNewClientData] = useState({ first_name: '', last_name: '', phone: '' });
  const [creatingClient, setCreatingClient] = useState(false);

  const handleCreateClientInline = async () => {
    const first_name = (newClientData.first_name || '').trim();
    const last_name = (newClientData.last_name || '').trim();
    const phone = (newClientData.phone || '').trim();
    if (!first_name || !last_name) {
      toast.error('Nombre y apellido son obligatorios');
      return;
    }
    setCreatingClient(true);
    try {
      const payload = cleanPayload({
        business_id: selectedBusiness?.id || localStorage.getItem('selectedBusinessId'),
        first_name,
        last_name,
        phone,
      });
      const { data: created } = await clientsAPI.create(payload);
      setClients((prev) => [created, ...prev]);
      setFormData((prev) => ({
        ...prev,
        client_id: created.id,
        client_name: `${created.first_name} ${created.last_name}`.trim(),
        client_phone: created.phone || '',
      }));
      setNewClientOpen(false);
      setNewClientData({ first_name: '', last_name: '', phone: '' });
      toast.success(`Cliente "${created.first_name}" creado y seleccionado`);
    } catch (error) {
      logger.error('Error creating client:', error);
      toast.error(getErrorMessage(error, 'No se pudo crear el cliente'));
    } finally {
      setCreatingClient(false);
    }
  };

  // Custom tags persisted per business in localStorage
  const customTagsKey = useMemo(() => {
    const bId = selectedBusiness?.id || localStorage.getItem('selectedBusinessId') || 'default';
    return `agenda_custom_tags_${bId}`;
  }, [selectedBusiness]);
  const [customTags, setCustomTags] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');

  // Custom statuses persisted per business in localStorage
  // Each: { id: string, label: string, color: string }
  const customStatusesKey = useMemo(() => {
    const bId = selectedBusiness?.id || localStorage.getItem('selectedBusinessId') || 'default';
    return `agenda_custom_statuses_${bId}`;
  }, [selectedBusiness]);
  const [customStatuses, setCustomStatuses] = useState([]);
  const [newStatusOpen, setNewStatusOpen] = useState(false);
  const [newStatusData, setNewStatusData] = useState({ label: '', color: '#a8d5a2' });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(customStatusesKey);
      setCustomStatuses(raw ? JSON.parse(raw) : []);
    } catch {
      setCustomStatuses([]);
    }
  }, [customStatusesKey]);

  const persistCustomStatuses = (next) => {
    setCustomStatuses(next);
    try {
      localStorage.setItem(customStatusesKey, JSON.stringify(next));
    } catch { /* ignore */ }
  };

  const addCustomStatus = () => {
    const label = (newStatusData.label || '').trim();
    if (!label) {
      toast.error('Ingresa un nombre para el estado');
      return;
    }
    if (label.length > 24) {
      toast.error('Máximo 24 caracteres');
      return;
    }
    const id = `cs_${Math.random().toString(36).slice(2)}`;
    const next = [...customStatuses, { id, label, color: newStatusData.color }];
    persistCustomStatuses(next);
    // Only add to the saved list — do NOT auto-apply to the current appointment.
    // The user can pick it manually from the Estado dropdown.
    setNewStatusData({ label: '', color: '#a8d5a2' });
    setNewStatusOpen(false);
    toast.success(`Estado "${label}" agregado a la lista`);
  };

  const removeCustomStatus = (id) => {
    const removed = customStatuses.find(s => s.id === id);
    persistCustomStatuses(customStatuses.filter(s => s.id !== id));
    if (removed && formData.status === 'custom' && formData.custom_status_label === removed.label) {
      setFormData((prev) => ({
        ...prev,
        status: 'pending',
        custom_status_label: '',
        custom_color: '',
      }));
    }
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(customTagsKey);
      setCustomTags(raw ? JSON.parse(raw) : []);
    } catch {
      setCustomTags([]);
    }
  }, [customTagsKey]);

  const persistCustomTags = (next) => {
    setCustomTags(next);
    try {
      localStorage.setItem(customTagsKey, JSON.stringify(next));
    } catch (e) {
      // localStorage might be unavailable in some browsers
    }
  };
  
  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    client_phone: '',
    barber_id: '',
    service_ids: [],
    date: '',
    start_time: '',
    end_time: '',
    status: 'pending',
    custom_status_label: '',
    is_admin: false,
    notes: '',
    tags: [],
    custom_color: '',
    visit_type: 'booked'
  });

  const gridRef = useRef(null);
  const columnRefs = useRef({});

  // Load data
  const loadData = useCallback(async () => {
    // Check for business ID in localStorage as fallback during initial load
    const savedBusinessId = localStorage.getItem('selectedBusinessId');
    const businessId = selectedBusiness?.id || savedBusinessId;
    
    if (!businessId) {
      console.log('No business selected, skipping data load');
      return;
    }
    
    // Ensure axios has the business ID header set
    if (!axios.defaults.headers.common['X-Business-Id']) {
      axios.defaults.headers.common['X-Business-Id'] = businessId;
    }
    
    try {
      setLoading(true);
      const [appointmentsRes, barbersRes, servicesRes, clientsRes] = await Promise.all([
        appointmentsAPI.getAll({ date: currentDate, ...(privateMode ? { only_private: true } : {}) }),
        barbersAPI.getAll(privateMode ? { only_private: true } : { exclude_admin_collab: true }),
        servicesAPI.getAll(),
        clientsAPI.getAll({ limit: 5000 })
      ]);
      
      setAppointments(appointmentsRes.data || []);
      setBarbers(barbersRes.data?.filter(b => b.is_active !== false) || []);
      setServices(servicesRes.data?.filter(s => s.is_active !== false) || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      logger.error('Error loading data:', error);
      // Only show toast if it's not an auth error (which would redirect)
      if (error.response?.status !== 401) {
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, currentDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Navigate dates (anchored to noon to avoid timezone shifts)
  const goToPrevDay = () => {
    const date = new Date(currentDate + 'T12:00:00');
    date.setDate(date.getDate() - 1);
    setCurrentDate(date.toLocaleDateString('en-CA'));
  };

  const goToNextDay = () => {
    const date = new Date(currentDate + 'T12:00:00');
    date.setDate(date.getDate() + 1);
    setCurrentDate(date.toLocaleDateString('en-CA'));
  };

  const goToToday = () => {
    setCurrentDate(getCDMXDate());
  };

  // Calculate time from Y position
  const getTimeFromY = (y, columnElement) => {
    if (!columnElement) return null;
    const rect = columnElement.getBoundingClientRect();
    const relativeY = y - rect.top;
    const slotIndex = Math.floor(relativeY / SLOT_HEIGHT);
    const clampedIndex = Math.max(0, Math.min(slotIndex, timeSlots.length - 1));
    return timeSlots[clampedIndex];
  };

  // Calculate appointment position and height (relative to first time slot in the grid)
  const getAppointmentStyle = (appointment) => {
    const startParts = appointment.start_time.split(':');
    const endParts = appointment.end_time.split(':');
    
    const startHour = parseInt(startParts[0]);
    const startMin = parseInt(startParts[1]);
    const endHour = parseInt(endParts[0]);
    const endMin = parseInt(endParts[1]);
    
    // Offset is relative to the first slot of the current grid (depends on business hours)
    const baseHour = timeSlots.length > 0 ? timeSlots[0].hour : 9;
    const startSlots = (startHour - baseHour) * 4 + Math.floor(startMin / 15);
    const endSlots = (endHour - baseHour) * 4 + Math.floor(endMin / 15);
    const durationSlots = Math.max(endSlots - startSlots, 1);
    
    return {
      top: `${startSlots * SLOT_HEIGHT}px`,
      height: `${durationSlots * SLOT_HEIGHT - 2}px`
    };
  };

  // Get appointment color (custom or status-based)
  const getAppointmentColor = (appointment) => {
    if (appointment.custom_color) {
      return {
        bgColor: appointment.custom_color,
        borderColor: appointment.custom_color,
        textColor: isLightColor(appointment.custom_color) ? '#1f2937' : '#ffffff'
      };
    }
    // Custom user-defined status: look up its saved color
    if (appointment.status === 'custom' && appointment.custom_status_label) {
      const cs = customStatuses.find(s => s.label === appointment.custom_status_label);
      if (cs) {
        return {
          bgColor: cs.color,
          borderColor: cs.color,
          textColor: isLightColor(cs.color) ? '#1f2937' : '#ffffff',
        };
      }
    }
    return STATUS_CONFIG[appointment.status] || STATUS_CONFIG.pending;
  };

  // Check if color is light
  const isLightColor = (color) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128;
  };

  // Get appointments for a specific barber on the currently-viewed day.
  // Filtering by date here is a defensive measure: even though the API call
  // is already scoped by date, this guarantees a cita created/edited for a
  // different day cannot leak into another day's view.
  const getBarberAppointments = (barberId) => {
    return appointments.filter(
      (apt) => apt.barber_id === barberId && apt.date === currentDate
    );
  };

  // Check for overlapping appointments
  const checkOverlap = useCallback((barberId, startTime, endTime, excludeAppointmentId = null) => {
    // Convert time strings to minutes for easier comparison
    const timeToMinutes = (time) => {
      const [hours, mins] = time.split(':').map(Number);
      return hours * 60 + mins;
    };
    
    const newStart = timeToMinutes(startTime);
    const newEnd = timeToMinutes(endTime);
    
    // Find all appointments for this barber on current date
    const barberAppointments = appointments.filter(apt => 
      apt.barber_id === barberId &&
      apt.date === currentDate &&
      apt.status !== 'cancelled' &&
      apt.id !== excludeAppointmentId
    );
    
    // Check for overlap with each existing appointment
    for (const apt of barberAppointments) {
      const existingStart = timeToMinutes(apt.start_time);
      const existingEnd = timeToMinutes(apt.end_time);
      
      // Check if there's any overlap
      // Overlap exists if: newStart < existingEnd AND newEnd > existingStart
      if (newStart < existingEnd && newEnd > existingStart) {
        return {
          hasOverlap: true,
          conflictingAppointment: apt
        };
      }
    }
    
    return { hasOverlap: false, conflictingAppointment: null };
  }, [appointments, currentDate]);

  // Get day of week name from date (in CDMX timezone)
  const getDayOfWeek = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
  };

  // Check if barber is on rest day or not working
  const isBarberRestDay = (barber) => {
    const dayOfWeek = getDayOfWeek(currentDate);
    
    // Check explicit rest_days (multi) or legacy rest_day (single)
    const restDays = Array.isArray(barber.rest_days) && barber.rest_days.length
      ? barber.rest_days
      : (barber.rest_day ? [barber.rest_day] : []);
    if (restDays.includes(dayOfWeek)) {
      return true;
    }
    
    // Check work_schedule
    if (barber.work_schedule && barber.work_schedule[dayOfWeek]) {
      return !barber.work_schedule[dayOfWeek].is_working;
    }
    
    return false;
  };

  // Get work hours for the current day. The BARBER's schedule (Configuración
  // → Barberos → Horarios) is the source of truth and takes precedence over
  // the business hours. We only fall back to the business window if the
  // barber has no specific schedule for that day.
  const getBarberWorkHours = (barber) => {
    const dayOfWeek = getDayOfWeek(currentDate);

    const bizDay = selectedBusiness?.schedule?.[dayOfWeek];
    const bizOpen = bizDay && bizDay.open && bizDay.open !== 'closed' ? bizDay.open : null;
    const bizClose = bizDay && bizDay.close && bizDay.close !== 'closed' ? bizDay.close : null;

    const barberDay = barber.work_schedule?.[dayOfWeek];
    const barberOpen = barberDay?.is_working ? (barberDay.start_time || null) : null;
    const barberClose = barberDay?.is_working ? (barberDay.end_time || null) : null;

    // Barber schedule wins. Fall back to business hours only when the barber
    // doesn't define their own window for the day.
    const start = barberOpen || bizOpen || '09:00';
    const end = barberClose || bizClose || '20:00';

    return { start, end };
  };

  // Check if a time slot is within barber's work hours
  const isSlotWithinWorkHours = (barber, slot) => {
    if (isBarberRestDay(barber)) return false;
    
    const workHours = getBarberWorkHours(barber);
    const slotTime = slot.label;
    
    return slotTime >= workHours.start && slotTime < workHours.end;
  };

  // Order barbers so those working the current day appear first (left), with
  // real barbers/stylists BEFORE admin users acting as sellers, and everyone
  // on rest day pushed to the end (right). Within each group we preserve the
  // original API order to keep configuration stable.
  const sortedBarbers = useMemo(() => {
    const staff = [];       // real barbers/stylists working today
    const admins = [];      // admin users acting as sellers, working today
    const resting = [];     // anyone whose day-off matches the current day
    for (const b of barbers) {
      // "Solo activos" toggle: hide barbers that are unavailable (red state)
      // or on rest day. Hard-inactive ones are already filtered at load.
      if (onlyActive && (b.is_available === false || isBarberRestDay(b))) {
        continue;
      }
      if (staffType !== 'all' && (b.employee_type || 'Barbería') !== staffType) {
        continue;
      }
      if (isBarberRestDay(b)) {
        resting.push(b);
      } else if (b.is_admin_seller) {
        admins.push(b);
      } else {
        staff.push(b);
      }
    }
    return [...staff, ...admins, ...resting];
  }, [barbers, currentDate, onlyActive, staffType]);


  // Handle cell click to create appointment
  const handleCellClick = (barberId, timeSlot) => {
    // Calculate default end time (30 min later)
    const endHour = timeSlot.minute >= 30 ? timeSlot.hour + 1 : timeSlot.hour;
    const endMin = (timeSlot.minute + 30) % 60;
    
    setSelectedSlot({ barberId, timeSlot });
    setSelectedAppointment(null);
    setFormData({
      client_id: '',
      client_name: '',
      client_phone: '',
      barber_id: barberId,
      service_ids: [],
      date: currentDate,
      start_time: timeSlot.label,
      end_time: `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`,
      status: 'pending',
      custom_status_label: '',
      is_admin: false,
      notes: '',
      tags: [],
      custom_color: '',
      allow_overlap: false,
      visit_type: 'booked'
    });
    setAppointmentDialogOpen(true);
  };

  // Handle appointment click - open detail dialog
  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setAppointmentHistory([]);
    setAppointmentTab('detalles');
    setFormData({
      client_id: appointment.client_id || '',
      client_name: appointment.client_name || '',
      client_phone: appointment.client_phone || '',
      barber_id: appointment.barber_id,
      service_ids: appointment.service_ids || [],
      date: appointment.date,
      start_time: appointment.start_time,
      end_time: appointment.end_time,
      status: appointment.status,
      custom_status_label: appointment.custom_status_label || '',
      is_admin: appointment.is_admin || false,
      notes: appointment.notes || '',
      tags: appointment.tags || [],
      custom_color: appointment.custom_color || '',
      allow_overlap: appointment.allow_overlap || false,
      visit_type: appointment.visit_type || 'booked'
    });
    setAppointmentDialogOpen(true);
  };

  // Mouse down on appointment - start potential drag
  const handleAppointmentMouseDown = (e, appointment) => {
    if (e.button !== 0) return; // Only left click
    
    e.preventDefault();
    mouseDownTimeRef.current = Date.now();
    mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
    
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setDragPosition({ x: e.clientX, y: e.clientY });
    
    // Set timer to activate drag mode after holding
    dragTimerRef.current = setTimeout(() => {
      setDraggingAppointment(appointment);
      setIsDragging(true);
      document.body.style.cursor = 'grabbing';
    }, DRAG_THRESHOLD);
  };

  // Mouse move handler
  const handleMouseMove = useCallback((e) => {
    // Check if we moved enough to cancel click
    if (dragTimerRef.current && !isDragging) {
      const dx = e.clientX - mouseDownPosRef.current.x;
      const dy = e.clientY - mouseDownPosRef.current.y;
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        // User is trying to drag before threshold - activate drag immediately
        clearTimeout(dragTimerRef.current);
        dragTimerRef.current = null;
      }
    }
    
    if (isDragging && draggingAppointment) {
      setDragPosition({ x: e.clientX, y: e.clientY });
    }
  }, [isDragging, draggingAppointment]);

  // Mouse up handler
  const handleMouseUp = useCallback(async (e) => {
    // Clear the drag timer
    if (dragTimerRef.current) {
      clearTimeout(dragTimerRef.current);
      dragTimerRef.current = null;
    }

    const timeDiff = Date.now() - mouseDownTimeRef.current;

    // If we were dragging, handle drop
    if (isDragging && draggingAppointment) {
      document.body.style.cursor = '';
      
      // Find which column we're over
      let targetBarberId = null;
      let targetTimeSlot = null;

      for (const [barberId, columnEl] of Object.entries(columnRefs.current)) {
        if (!columnEl) continue;
        const rect = columnEl.getBoundingClientRect();
        if (e.clientX >= rect.left && e.clientX <= rect.right) {
          targetBarberId = barberId;
          targetTimeSlot = getTimeFromY(e.clientY, columnEl);
          break;
        }
      }

      if (targetBarberId && targetTimeSlot) {
        // Calculate new times
        const startParts = draggingAppointment.start_time.split(':');
        const endParts = draggingAppointment.end_time.split(':');
        const durationMinutes = 
          (parseInt(endParts[0]) * 60 + parseInt(endParts[1])) - 
          (parseInt(startParts[0]) * 60 + parseInt(startParts[1]));
        
        const newStartMinutes = targetTimeSlot.hour * 60 + targetTimeSlot.minute;
        const newEndMinutes = newStartMinutes + durationMinutes;
        const newEndHour = Math.floor(newEndMinutes / 60);
        const newEndMin = newEndMinutes % 60;
        
        const newStartTime = targetTimeSlot.label;
        const newEndTime = `${String(newEndHour).padStart(2, '0')}:${String(newEndMin).padStart(2, '0')}`;

        // Check if within bounds (9:00 - 20:00)
        if (newEndHour > 20) {
          toast.error('La cita excede el horario de trabajo');
        } else {
          // Check for overlap (unless it's an admin appointment or has allow_overlap)
          const allowOverlap = draggingAppointment.allow_overlap || draggingAppointment.is_admin;
          
          if (!allowOverlap) {
            const { hasOverlap, conflictingAppointment } = checkOverlap(
              targetBarberId,
              newStartTime,
              newEndTime,
              draggingAppointment.id
            );
            
            if (hasOverlap) {
              const barberName = barbers.find(b => b.id === targetBarberId)?.display_name || 'El profesional';
              toast.error(
                `${barberName} ya tiene una cita de ${conflictingAppointment.start_time} a ${conflictingAppointment.end_time}`,
                { duration: 4000 }
              );
              setDraggingAppointment(null);
              setIsDragging(false);
              setDragOffset({ x: 0, y: 0 });
              setDragPosition({ x: 0, y: 0 });
              return;
            }
          }
          
          try {
            await appointmentsAPI.update(draggingAppointment.id, {
              barber_id: targetBarberId,
              start_time: newStartTime,
              end_time: newEndTime
            });
            toast.success('Cita movida correctamente');
            reloadAppointments();
          } catch (error) {
            toast.error('Error al mover la cita');
          }
        }
      }

      setDraggingAppointment(null);
      setIsDragging(false);
      setDragOffset({ x: 0, y: 0 });
      setDragPosition({ x: 0, y: 0 });
    }
  }, [isDragging, draggingAppointment, loadData]);

  // Resize handlers
  const handleResizeStart = (e, appointment) => {
    e.stopPropagation();
    e.preventDefault();
    setResizingAppointment(appointment);
    setResizeStartY(e.clientY);
    document.body.style.cursor = 'ns-resize';
  };

  const handleResizeEnd = useCallback(async (e) => {
    if (!resizingAppointment) return;

    document.body.style.cursor = '';
    
    const deltaY = e.clientY - resizeStartY;
    const deltaSlots = Math.round(deltaY / SLOT_HEIGHT);
    
    if (deltaSlots !== 0) {
      const endParts = resizingAppointment.end_time.split(':');
      const currentEndMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
      const newEndMinutes = currentEndMinutes + (deltaSlots * 15);
      
      // Minimum 15 minutes, max 20:00
      const startParts = resizingAppointment.start_time.split(':');
      const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
      
      if (newEndMinutes > startMinutes && newEndMinutes <= 20 * 60) {
        const newEndHour = Math.floor(newEndMinutes / 60);
        const newEndMin = newEndMinutes % 60;
        const newEndTime = `${String(newEndHour).padStart(2, '0')}:${String(newEndMin).padStart(2, '0')}`;
        
        // Check for overlap only if extending (not shrinking)
        if (deltaSlots > 0) {
          const allowOverlap = resizingAppointment.allow_overlap || resizingAppointment.is_admin;
          
          if (!allowOverlap) {
            const { hasOverlap, conflictingAppointment } = checkOverlap(
              resizingAppointment.barber_id,
              resizingAppointment.start_time,
              newEndTime,
              resizingAppointment.id
            );
            
            if (hasOverlap) {
              toast.error(`Empalme con cita de ${conflictingAppointment.start_time} a ${conflictingAppointment.end_time}`);
              setResizingAppointment(null);
              setResizeStartY(0);
              return;
            }
          }
        }
        
        try {
          await appointmentsAPI.update(resizingAppointment.id, {
            end_time: newEndTime
          });
          toast.success('Duración actualizada');
          reloadAppointments();
        } catch (error) {
          toast.error('Error al actualizar duración');
        }
      }
    }

    setResizingAppointment(null);
    setResizeStartY(0);
  }, [resizingAppointment, resizeStartY, loadData, checkOverlap]);

  // Global mouse event listeners
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (dragTimerRef.current) {
        clearTimeout(dragTimerRef.current);
      }
    };
  }, [handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (resizingAppointment) {
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizingAppointment, handleResizeEnd]);

  // Quick action handlers
  const handleQuickStatusChange = async (appointmentId, newStatus) => {
    try {
      await appointmentsAPI.update(appointmentId, { status: newStatus });
      toast.success(`Estado: ${STATUS_CONFIG[newStatus]?.label}`);
      reloadAppointments();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleQuickColorChange = async (appointmentId, color) => {
    try {
      await appointmentsAPI.update(appointmentId, { custom_color: color });
      toast.success('Color actualizado');
      reloadAppointments();
    } catch (error) {
      toast.error('Error al actualizar color');
    }
  };

  // Toggle tag
  const toggleTag = (tag) => {
    const newTags = formData.tags.includes(tag)
      ? formData.tags.filter(t => t !== tag)
      : [...formData.tags, tag];
    setFormData({ ...formData, tags: newTags });
  };

  // Add a new custom tag (persists to localStorage and selects it)
  const addCustomTag = () => {
    const value = newTagInput.trim();
    if (!value) return;
    if (value.length > 24) {
      toast.error('La etiqueta es muy larga (máx 24 caracteres)');
      return;
    }
    const exists =
      COMMON_TAGS.some(t => t.toLowerCase() === value.toLowerCase()) ||
      customTags.some(t => t.toLowerCase() === value.toLowerCase());
    if (!exists) {
      persistCustomTags([...customTags, value]);
    }
    // Auto-select for current appointment if not already
    if (!formData.tags.includes(value)) {
      setFormData({ ...formData, tags: [...formData.tags, value] });
    }
    setNewTagInput('');
  };

  // Remove a custom tag from the saved list (also removes from current selection)
  const removeCustomTag = (tag) => {
    persistCustomTags(customTags.filter(t => t !== tag));
    if (formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
    }
  };

  // Save appointment
  // Lightweight refresh: only reload appointments for the current date.
  // Used after save/delete or by the auto-refresh interval to avoid heavy reloads.
  const reloadAppointments = useCallback(async () => {
    const businessId = selectedBusiness?.id || localStorage.getItem('selectedBusinessId');
    if (!businessId) return;
    try {
      const res = await appointmentsAPI.getAll({ date: currentDate, ...(privateMode ? { only_private: true } : {}) });
      setAppointments(res.data || []);
    } catch (error) {
      logger.error('Error refreshing appointments:', error);
    }
  }, [selectedBusiness, currentDate, privateMode]);

  // Auto-refresh appointments every 30s while page is visible (real-time-ish)
  useEffect(() => {
    if (!selectedBusiness) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') {
        reloadAppointments();
      }
    }, 30000);
    return () => clearInterval(id);
  }, [selectedBusiness, reloadAppointments]);

  const handleSaveAppointment = async () => {
    if (!formData.barber_id || !formData.start_time || !formData.end_time) {
      toast.error('Completa los campos requeridos');
      return;
    }

    // Validate that end time is after start time
    const startMinutes = formData.start_time.split(':').reduce((h, m) => parseInt(h) * 60 + parseInt(m));
    const endMinutes = formData.end_time.split(':').reduce((h, m) => parseInt(h) * 60 + parseInt(m));
    
    if (endMinutes <= startMinutes) {
      toast.error('La hora de fin debe ser posterior a la hora de inicio');
      return;
    }

    // Check for overlapping appointments (unless allow_overlap is checked).
    // Local overlap check only runs when the appointment stays on the date
    // currently loaded in the grid; cross-date moves are validated by the API.
    if (!formData.allow_overlap && (formData.date || currentDate) === currentDate) {
      const { hasOverlap, conflictingAppointment } = checkOverlap(
        formData.barber_id,
        formData.start_time,
        formData.end_time,
        selectedAppointment?.id
      );
      
      if (hasOverlap) {
        const barberName = barbers.find(b => b.id === formData.barber_id)?.display_name || 'El profesional';
        toast.error(
          `${barberName} ya tiene una cita de ${conflictingAppointment.start_time} a ${conflictingAppointment.end_time}. Marca "Permitir empalme" si deseas crear la cita de todas formas.`,
          { duration: 5000 }
        );
        return;
      }
    }

    try {
      const appointmentData = {
        ...formData,
        // Preserve the appointment's own date when editing; only use currentDate for new ones
        date: formData.date || currentDate,
        client_name: formData.client_name || 'Sin nombre',
        service_names: formData.service_ids.map(sid => 
          services.find(s => s.id === sid)?.name
        ).filter(Boolean)
      };

      if (selectedAppointment) {
        await appointmentsAPI.update(selectedAppointment.id, appointmentData);
        toast.success('Cita actualizada');
      } else {
        await appointmentsAPI.create(appointmentData);
        toast.success('Cita creada');
      }
      
      setAppointmentDialogOpen(false);
      // Only reload appointments (lightweight); barbers/services/clients don't change here
      reloadAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  // Delete appointment (permanent, no confirmation)
  const handleDeleteAppointment = async () => {
    if (!selectedAppointment) return;
    try {
      await appointmentsAPI.delete(selectedAppointment.id);
      toast.success('Cita eliminada');
      setAppointmentDialogOpen(false);
      reloadAppointments();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Quick-delete from the appointment card corner (no confirmation, no dialog)
  const handleQuickDelete = useCallback(async (appointmentId) => {
    try {
      await appointmentsAPI.delete(appointmentId);
      toast.success('Cita eliminada');
      reloadAppointments();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  }, [reloadAppointments]);

  // Generate time options
  const generateTimeOptions = () => {
    const options = [];
    for (let h = 9; h <= 20; h++) {
      for (let m = 0; m < 60; m += 15) {
        if (h === 20 && m > 0) break;
        options.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      }
    }
    return options;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div
      className={cn('h-full flex flex-col', privateMode && 'private-agenda-scope')}
      data-testid={privateMode ? 'private-agenda-page' : 'agenda-grid-page'}
    >
      {/* Header */}
      <div
        className={cn(
          'flex items-center justify-between p-4 border-b rounded-t-lg',
          privateMode
            ? 'bg-slate-900 border-slate-900 text-white'
            : 'bg-card border-border'
        )}
      >
        <div className="flex items-center gap-4">
          <h1
            className={cn(
              'text-2xl font-bold flex items-center gap-2',
              privateMode ? 'text-white' : 'text-foreground'
            )}
            style={{ fontFamily: 'Manrope' }}
            data-testid="agenda-title"
          >
            {privateMode && <Lock className="w-5 h-5" />}
            {privateMode ? 'Agenda privada' : 'Agenda'}
          </h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={goToPrevDay} data-testid="prev-day-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={goToToday} data-testid="today-btn">
              Hoy
            </Button>
            <Button variant="outline" size="icon" onClick={goToNextDay} data-testid="next-day-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/70 transition cursor-pointer border border-transparent hover:border-slate-200"
                data-testid="date-picker-trigger"
                title="Ir a otra fecha"
              >
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium text-foreground">{formatDate(currentDate)}</span>
                <span className="text-xs text-muted-foreground">(CDMX GMT-6)</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={new Date(currentDate + 'T12:00:00')}
                onSelect={(d) => {
                  if (!d) return;
                  const y = d.getFullYear();
                  const m = String(d.getMonth() + 1).padStart(2, '0');
                  const day = String(d.getDate()).padStart(2, '0');
                  setCurrentDate(`${y}-${m}-${day}`);
                }}
                initialFocus
                data-testid="date-picker-calendar"
              />
            </PopoverContent>
          </Popover>
        </div>
        
        {/* Status legend */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Barbería / Salón / Todos segmented toggle */}
          <div className="inline-flex rounded-md border border-slate-200 bg-slate-100 p-0.5" data-testid="staff-type-toggle">
            {[
              { v: 'Barbería', label: 'Barbería', on: 'bg-amber-600 text-white' },
              { v: 'Salón', label: 'Salón', on: 'bg-rose-600 text-white' },
              { v: 'all', label: 'Todos', on: 'bg-slate-900 text-white' },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => setStaffType(o.v)}
                className={cn(
                  "px-3 py-1 text-xs font-medium rounded transition-colors",
                  staffType === o.v ? o.on : "text-slate-600 hover:bg-white"
                )}
                data-testid={`staff-type-${o.v === 'all' ? 'todos' : o.v === 'Salón' ? 'salon' : 'barberia'}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="h-5 w-px bg-slate-200" />
          {/* "Solo activos" toggle (hides barbers in rest-day / unavailable state) */}
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="only-active-toggle">
            <Switch
              checked={onlyActive}
              onCheckedChange={setOnlyActive}
              className="data-[state=checked]:bg-emerald-600"
            />
            <span className="text-xs font-medium text-slate-600">Solo activos</span>
          </label>
          <div className="h-5 w-px bg-slate-200" />
          {isAdmin() && hasPrivateBarbers && !privateMode && (
            <Button
              variant="outline"
              size="sm"
              className="border-slate-900 bg-slate-900 text-white hover:bg-slate-800"
              onClick={() => navigate('/agenda/privada')}
              data-testid="private-agenda-btn"
              title="Agenda privada — solo master admin"
            >
              🔒 Agenda privada
            </Button>
          )}
          {privateMode && (
            <Button
              variant="outline"
              size="sm"
              className="border-slate-200 text-slate-600 hover:bg-slate-50"
              onClick={() => navigate('/agenda')}
              data-testid="back-to-public-agenda-btn"
              title="Volver a agenda pública"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Agenda pública
            </Button>
          )}
          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
            <div key={key} className="flex items-center gap-1">
              <div 
                className="w-3 h-3 rounded border" 
                style={{ backgroundColor: config.bgColor, borderColor: config.borderColor }}
              />
              <span className="text-xs text-muted-foreground">{config.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Drag hint */}
      <div className="px-4 py-1 bg-muted/50 text-xs text-muted-foreground flex items-center gap-2">
        <Move className="w-3 h-3" />
        <span>Mantén presionado para arrastrar citas • Clic para ver detalles</span>
      </div>

      {/* Empty state (private mode without private barbers) */}
      {privateMode && barbers.length === 0 && (
        <div className="flex-1 flex items-center justify-center bg-white border border-t-0 border-border rounded-b-lg">
          <div className="text-center max-w-sm py-16 px-6">
            <Lock className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <p className="font-semibold text-slate-700" style={{ fontFamily: 'Manrope' }}>
              No hay barberos privados
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Marca un barbero con el flag <span className="font-mono bg-slate-100 px-1 rounded">is_private</span> desde
              Barberos para que aparezca aquí.
            </p>
          </div>
        </div>
      )}

      {/* Empty state for the Barbería / Salón filter */}
      {!privateMode && barbers.length > 0 && sortedBarbers.length === 0 && (
        <div className="px-4 py-3 bg-amber-50 border border-t-0 border-amber-200 text-sm text-amber-800" data-testid="staff-type-empty">
          No hay empleados {staffType === 'all' ? 'activos' : `de ${staffType}`} para mostrar en este día.
        </div>
      )}

      {/* Schedule Grid */}
      {!(privateMode && barbers.length === 0) && (
      <div className="flex-1 overflow-auto bg-card rounded-b-lg border border-t-0 border-border" ref={gridRef}>
        <div className="min-w-max">
          {/* Barber Headers */}
          <div className="flex sticky top-0 z-30 bg-card border-b-2 border-border">
            {/* Time column header */}
            <div className="w-20 min-w-20 p-2 border-r-2 border-border bg-muted flex items-center justify-center">
              <Clock className="w-4 h-4 text-muted-foreground" />
            </div>
            
            {/* Barber columns */}
            {sortedBarbers.map(barber => {
              const isRestDay = isBarberRestDay(barber);
              const workHours = !isRestDay ? getBarberWorkHours(barber) : null;
              
              return (
                <div 
                  key={barber.id} 
                  className={cn(
                    "flex-1 min-w-36 p-3 border-r border-border text-center",
                    isRestDay 
                      ? "bg-red-50" 
                      : "bg-gradient-to-b from-muted/50 to-card"
                  )}
                >
                  <Avatar className={cn(
                    "w-10 h-10 mx-auto mb-1 border-2",
                    isRestDay ? "border-red-300 opacity-60" : "border-primary/20"
                  )}>
                    <AvatarImage src={barber.avatar_url} />
                    <AvatarFallback className={cn(
                      "text-sm font-medium",
                      isRestDay ? "bg-red-100 text-red-600" : "bg-primary/10 text-primary"
                    )}>
                      {barber.display_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <p className={cn(
                    "font-medium text-sm truncate",
                    isRestDay ? "text-red-600" : "text-foreground"
                  )}>
                    {barber.display_name}
                  </p>
                  {isRestDay && (
                    <span className="text-[10px] text-red-500 font-medium">
                      Día de descanso
                    </span>
                  )}
                  {!isRestDay && workHours && (
                    <span className="text-[10px] text-muted-foreground">
                      {workHours.start} - {workHours.end}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Time slots grid */}
          <div className="flex">
            {/* Time labels column - Show ALL 15-minute labels */}
            <div className="w-20 min-w-20 border-r-2 border-border bg-muted/30">
              {timeSlots.map((slot, idx) => (
                <div 
                  key={slot.label}
                  className={cn(
                    "pr-2 flex items-center justify-end text-xs font-mono",
                    slot.isHourStart 
                      ? "border-t-2 border-border font-bold text-foreground bg-muted/50" 
                      : "text-muted-foreground"
                  )}
                  style={{ height: `${SLOT_HEIGHT}px` }}
                >
                  {slot.label}
                </div>
              ))}
            </div>

            {/* Barber columns with appointments */}
            {sortedBarbers.map(barber => {
              const isRestDay = isBarberRestDay(barber);
              
              return (
                <div 
                  key={barber.id} 
                  className={cn(
                    "flex-1 min-w-36 border-r border-border relative",
                    isRestDay && "bg-red-50/50"
                  )}
                  ref={el => columnRefs.current[barber.id] = el}
                >
                  {/* Rest day overlay */}
                  {isRestDay && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center pointer-events-none">
                      <div className="bg-red-100/80 rounded-lg px-4 py-2 shadow-sm border border-red-200">
                        <span className="text-red-600 font-medium text-sm">Día de descanso</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Time slots */}
                  {timeSlots.map((slot, idx) => {
                    const isWithinWorkHours = isSlotWithinWorkHours(barber, slot);
                    const isOutsideHours = !isRestDay && !isWithinWorkHours;
                    
                    return (
                      <div
                        key={slot.label}
                        className={cn(
                          "border-b border-border/30 transition-colors",
                          slot.isHourStart && "border-t border-border",
                          isRestDay && "bg-red-50/70 cursor-not-allowed",
                          isOutsideHours && "bg-slate-100/70 cursor-not-allowed",
                          !isRestDay && isWithinWorkHours && "hover:bg-primary/5 cursor-pointer"
                        )}
                        style={{ height: `${SLOT_HEIGHT}px` }}
                        onClick={() => {
                          if (!isRestDay && isWithinWorkHours) {
                            handleCellClick(barber.id, slot);
                          }
                        }}
                        data-testid={`cell-${barber.id}-${slot.label}`}
                      />
                    );
                  })}

                  {/* Appointments overlay */}
                  <div className="absolute inset-0 pointer-events-none">
                    {getBarberAppointments(barber.id).map(appointment => {
                    const style = getAppointmentStyle(appointment);
                    const colorConfig = getAppointmentColor(appointment);
                    const isBeingDragged = isDragging && draggingAppointment?.id === appointment.id;
                    
                    return (
                      <div
                        key={appointment.id}
                        className={cn(
                          "absolute left-1 right-1 rounded-md px-2 py-1 pointer-events-auto select-none transition-shadow group",
                          isBeingDragged && "opacity-50 shadow-lg",
                          !isDragging && "cursor-pointer hover:shadow-md"
                        )}
                        style={{
                          ...style,
                          backgroundColor: colorConfig.bgColor,
                          borderLeft: `4px solid ${colorConfig.borderColor}`,
                          color: colorConfig.textColor
                        }}
                        onMouseDown={(e) => handleAppointmentMouseDown(e, appointment)}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Only handle click if not dragging
                          if (!isDragging) {
                            handleAppointmentClick(appointment);
                          }
                        }}
                        title={`${appointment.client_name}\n${appointment.service_names?.join(', ')}\n${appointment.start_time} - ${appointment.end_time}`}
                        data-testid={`appointment-${appointment.id}`}
                      >
                        {/* Cobrar button (top-right corner, hidden when completed/cancelled) */}
                        {!['completed', 'cancelled', 'no_show', 'admin'].includes(appointment.status) && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/pos?appointment=${appointment.id}`);
                            }}
                            className="absolute top-1 right-12 z-10 flex items-center gap-0.5 rounded px-1.5 py-0.5 bg-white/95 text-emerald-700 border border-emerald-200 text-[9px] font-semibold opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-emerald-600 hover:text-white hover:border-emerald-600"
                            title="Cobrar en POS"
                            data-testid={`pay-appointment-${appointment.id}`}
                          >
                            <CreditCard className="w-2.5 h-2.5" />
                            Cobrar
                          </button>
                        )}
                        {/* Quick delete (X) — top-right corner, no confirmation */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleQuickDelete(appointment.id);
                          }}
                          className="absolute top-1 right-1 z-10 flex items-center justify-center w-5 h-5 rounded-full bg-white/95 text-red-600 border border-red-200 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-600 hover:text-white hover:border-red-600"
                          title="Eliminar cita"
                          data-testid={`delete-appointment-${appointment.id}`}
                        >
                          <X className="w-3 h-3" strokeWidth={3} />
                        </button>

                        {/* Bottom-right: status dropdown (always visible, 3-dot menu) */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="absolute bottom-1 right-1 z-10 flex items-center justify-center w-5 h-5 rounded-full bg-white/85 text-slate-700 border border-slate-200 shadow-sm hover:bg-white hover:text-slate-900 transition-colors"
                              title="Cambiar estado"
                              data-testid={`status-menu-${appointment.id}`}
                            >
                              <MoreHorizontal className="w-3 h-3" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuLabel>Cambiar estado</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                              const isActive = appointment.status === key;
                              return (
                                <DropdownMenuItem
                                  key={key}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuickStatusChange(appointment.id, key);
                                  }}
                                  className={isActive ? 'bg-slate-100 font-medium' : ''}
                                  data-testid={`status-option-${key}-${appointment.id}`}
                                >
                                  <div
                                    className="w-3 h-3 rounded mr-2 border"
                                    style={{ backgroundColor: config.bgColor, borderColor: config.borderColor }}
                                  />
                                  {config.label}
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <div className="flex items-start justify-between gap-1">
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <div className="font-semibold truncate text-xs leading-tight flex items-center gap-1">
                              {clients.find((c) => c.id === appointment.client_id)?.loyalty_enrolled && (
                                <Gift
                                  className="w-3 h-3 text-rose-500 shrink-0"
                                  aria-label="Club Barrabás"
                                  data-testid={`club-badge-${appointment.id}`}
                                />
                              )}
                              <span className="truncate">{appointment.client_name || 'Sin nombre'}</span>
                            </div>
                            <div className="truncate text-[10px] opacity-80 leading-tight">
                              {appointment.service_names?.join(', ') || 'Sin servicio'}
                            </div>
                            <div className="text-[10px] opacity-70 leading-tight">
                              {appointment.start_time} - {appointment.end_time}
                            </div>
                            {/* Tags */}
                            {appointment.tags?.length > 0 && (
                              <div className="flex gap-1 mt-0.5 flex-wrap">
                                {appointment.tags.slice(0, 2).map(tag => (
                                  <span key={tag} className="px-1 py-0 text-[8px] bg-white/30 rounded">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          {/* Quick actions menu */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                              <button 
                                className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-white/20 transition-opacity"
                              >
                                <MoreVertical className="w-3 h-3" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuLabel>Cambiar estado</DropdownMenuLabel>
                              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                                <DropdownMenuItem 
                                  key={key}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuickStatusChange(appointment.id, key);
                                  }}
                                >
                                  <div 
                                    className="w-3 h-3 rounded mr-2 border" 
                                    style={{ backgroundColor: config.bgColor, borderColor: config.borderColor }}
                                  />
                                  {config.label}
                                </DropdownMenuItem>
                              ))}
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel>Color personalizado</DropdownMenuLabel>
                              <div className="grid grid-cols-6 gap-1 p-2">
                                {PRESET_COLORS.map(color => (
                                  <button
                                    key={color}
                                    className="w-5 h-5 rounded border border-slate-300 hover:scale-110 transition-transform"
                                    style={{ backgroundColor: color }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleQuickColorChange(appointment.id, color);
                                    }}
                                  />
                                ))}
                              </div>
                              <button
                                className="w-full px-2 py-1 text-xs text-center text-muted-foreground hover:bg-muted"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQuickColorChange(appointment.id, null);
                                }}
                              >
                                Quitar color personalizado
                              </button>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                handleAppointmentClick(appointment);
                              }}>
                                <Edit className="w-3 h-3 mr-2" />
                                Editar cita
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        
                        {/* Resize handle */}
                        <div 
                          className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-white/20 rounded-b"
                          onMouseDown={(e) => handleResizeStart(e, appointment)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="w-8 h-1 mx-auto mt-0.5 rounded-full bg-current opacity-30" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>
      )}

      {/* Floating dragged appointment */}
      {isDragging && draggingAppointment && (
        <div
          className="fixed pointer-events-none z-50 rounded-md px-2 py-1 shadow-2xl"
          style={{
            left: dragPosition.x - dragOffset.x,
            top: dragPosition.y - dragOffset.y,
            width: '140px',
            backgroundColor: getAppointmentColor(draggingAppointment).bgColor,
            borderLeft: `4px solid ${getAppointmentColor(draggingAppointment).borderColor}`,
            color: getAppointmentColor(draggingAppointment).textColor
          }}
        >
          <div className="font-semibold truncate text-xs">
            {draggingAppointment.client_name || 'Sin nombre'}
          </div>
          <div className="truncate text-[10px] opacity-80">
            {draggingAppointment.start_time} - {draggingAppointment.end_time}
          </div>
        </div>
      )}

      {/* Appointment Dialog */}
      <Dialog open={appointmentDialogOpen} onOpenChange={(v) => {
        setAppointmentDialogOpen(v);
        if (v) { setAppointmentTab('detalles'); } else { setAppointmentHistory([]); }
      }}>
        <DialogContent className="max-w-lg !max-h-[90vh] !flex !flex-col !overflow-hidden p-0 !grid-flow-row" aria-describedby="appointment-dialog-description">
          <DialogHeader className="p-6 pb-0 shrink-0">
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {selectedAppointment ? 'Editar cita' : 'Nueva cita'}
            </DialogTitle>
            <p id="appointment-dialog-description" className="sr-only">
              Formulario para crear o editar una cita
            </p>
          </DialogHeader>

          <Tabs value={appointmentTab} onValueChange={async (v) => {
            setAppointmentTab(v);
            if (v === 'historial' && selectedAppointment) {
              setHistoryLoading(true);
              try {
                const { data } = await appointmentsAPI.history(selectedAppointment.id);
                setAppointmentHistory(data || []);
              } catch (err) {
                toast.error('No se pudo cargar el historial');
              } finally {
                setHistoryLoading(false);
              }
            }
          }} className="flex-1 flex flex-col overflow-hidden min-h-0">
            {selectedAppointment && (
              <TabsList className="mx-6 mt-3 grid grid-cols-2 w-auto shrink-0" data-testid="appointment-tabs">
                <TabsTrigger value="detalles" data-testid="tab-detalles">Detalles</TabsTrigger>
                <TabsTrigger
                  value="historial"
                  data-testid="tab-historial"
                >
                  <HistoryIcon className="w-4 h-4 mr-1" /> Historial
                </TabsTrigger>
              </TabsList>
            )}

            <TabsContent value="detalles" className="mt-3 flex-1 overflow-hidden">
              <div className={cn(
                "overflow-y-auto overscroll-contain scrollbar-visible",
                selectedAppointment ? "max-h-[calc(90vh-220px)]" : "max-h-[calc(90vh-160px)]",
              )}>
                <div className="px-6 py-4 space-y-4">
              {/* Client info */}
              <div className="space-y-2">
                <Label>Buscar cliente existente</Label>
                <SearchableSelect
                  items={clients}
                  value={formData.client_id || ''}
                  onChange={(client) => {
                    if (client) {
                      setFormData({
                        ...formData,
                        client_id: client.id,
                        client_name: `${client.first_name || ''} ${client.last_name || ''}`.trim(),
                        client_phone: client.phone || ''
                      });
                    } else {
                      setFormData({ ...formData, client_id: '', client_name: '', client_phone: '' });
                    }
                  }}
                  getLabel={(c) => `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.phone || 'Sin nombre'}
                  getSubLabel={(c) => c.phone || ''}
                  searchFields={(c) => [c.first_name, c.last_name, c.phone, `${c.first_name || ''} ${c.last_name || ''}`]}
                  placeholder="Buscar por nombre o teléfono..."
                  emptyLabel="Sin clientes registrados"
                  testId="appointment-client-search"
                  // eslint-disable-next-line react/no-unstable-nested-components
                  footer={({ close }) => (
                    <button
                      type="button"
                      onClick={() => {
                        close();
                        setNewClientData({ first_name: '', last_name: '', phone: '' });
                        setNewClientOpen(true);
                      }}
                      className="flex items-center justify-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
                      data-testid="agenda-new-client-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Nuevo cliente
                    </button>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre del cliente</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value, client_id: '' })}
                    placeholder="Nombre"
                    data-testid="client-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    placeholder="10 dígitos"
                    data-testid="client-phone-input"
                  />
                </div>
              </div>

              {/* Barber */}
              <div className="space-y-2">
                <Label>Profesional *</Label>
                <Select 
                  value={formData.barber_id} 
                  onValueChange={(value) => setFormData({ ...formData, barber_id: value })}
                >
                  <SelectTrigger data-testid="barber-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedBarbers.map(barber => (
                      <SelectItem key={barber.id} value={barber.id}>
                        {barber.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Services (multi-select) */}
              <div className="space-y-2">
                <Label>Servicios</Label>
                <SearchableSelect
                  items={services.filter((s) => !(formData.service_ids || []).includes(s.id))}
                  value=""
                  onChange={(svc) => {
                    if (!svc) return;
                    const current = formData.service_ids || [];
                    if (current.includes(svc.id)) return;
                    const nextIds = [...current, svc.id];
                    // Full recompute: end = start + Σ duration of all selected services.
                    let nextEnd = formData.end_time;
                    if (formData.start_time) {
                      const [sh, sm] = formData.start_time.split(':').map(Number);
                      const totalMin = nextIds.reduce((acc, sid) => {
                        const s = services.find((x) => x.id === sid);
                        return acc + Number(s?.duration_minutes || 0);
                      }, 0);
                      if (totalMin > 0) {
                        const endAbs = sh * 60 + sm + totalMin;
                        nextEnd = `${String(Math.floor(endAbs / 60)).padStart(2, '0')}:${String(endAbs % 60).padStart(2, '0')}`;
                      }
                    }
                    setFormData({ ...formData, service_ids: nextIds, end_time: nextEnd });
                  }}
                  getLabel={(s) => s.name}
                  getSubLabel={(s) => `${formatCurrency(s.price)} · ${s.duration_minutes}min${s.category ? ' · ' + s.category : ''}`}
                  searchFields={(s) => [s.name, s.category, s.description]}
                  placeholder="Buscar y agregar servicios…"
                  emptyLabel="Sin servicios disponibles"
                  testId="appointment-service-search"
                />

                {(formData.service_ids || []).length > 0 && (() => {
                  const picked = (formData.service_ids || [])
                    .map((sid) => services.find((s) => s.id === sid))
                    .filter(Boolean);
                  const totalPrice = picked.reduce((acc, s) => acc + Number(s.price || 0), 0);
                  const totalMinutes = picked.reduce((acc, s) => acc + Number(s.duration_minutes || 0), 0);
                  return (
                    <div
                      className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50/40 p-3"
                      data-testid="appointment-selected-services"
                    >
                      <div className="flex flex-col gap-1.5">
                        {picked.map((s) => (
                          <div
                            key={s.id}
                            className="flex items-center justify-between gap-2 rounded border border-emerald-200 bg-white px-2 py-1.5"
                            data-testid={`selected-service-${s.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-800 truncate">{s.name}</div>
                              <div className="text-[11px] text-slate-500">
                                {formatCurrency(s.price)} · {s.duration_minutes}min
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const nextIds = (formData.service_ids || []).filter((id) => id !== s.id);
                                let nextEnd = formData.end_time;
                                if (formData.start_time) {
                                  const [sh, sm] = formData.start_time.split(':').map(Number);
                                  const totalMin = nextIds.reduce((acc, sid) => {
                                    const sv = services.find((x) => x.id === sid);
                                    return acc + Number(sv?.duration_minutes || 0);
                                  }, 0);
                                  if (totalMin > 0) {
                                    const endAbs = sh * 60 + sm + totalMin;
                                    nextEnd = `${String(Math.floor(endAbs / 60)).padStart(2, '0')}:${String(endAbs % 60).padStart(2, '0')}`;
                                  }
                                }
                                setFormData({ ...formData, service_ids: nextIds, end_time: nextEnd });
                              }}
                              className="text-slate-400 hover:text-rose-600 text-lg leading-none px-1"
                              aria-label={`Quitar ${s.name}`}
                              data-testid={`remove-service-${s.id}`}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between pt-1 text-sm font-semibold text-emerald-800 border-t border-emerald-200">
                        <span>Total ({picked.length} serv. · {totalMinutes}min)</span>
                        <span data-testid="appointment-services-total">{formatCurrency(totalPrice)}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Date + Time */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Fecha *</Label>
                  <Input
                    type="date"
                    value={formData.date || currentDate}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    data-testid="appointment-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hora inicio *</Label>
                  <Select 
                    value={formData.start_time} 
                    onValueChange={(value) => {
                      // Auto-recalc end_time from the sum of selected service durations.
                      let nextEnd = formData.end_time;
                      const ids = formData.service_ids || [];
                      if (value && ids.length > 0) {
                        const totalMin = ids.reduce((acc, sid) => {
                          const sv = services.find((x) => x.id === sid);
                          return acc + Number(sv?.duration_minutes || 0);
                        }, 0);
                        if (totalMin > 0) {
                          const [sh, sm] = value.split(':').map(Number);
                          const endAbs = sh * 60 + sm + totalMin;
                          nextEnd = `${String(Math.floor(endAbs / 60)).padStart(2, '0')}:${String(endAbs % 60).padStart(2, '0')}`;
                        }
                      }
                      setFormData({ ...formData, start_time: value, end_time: nextEnd });
                    }}
                  >
                    <SelectTrigger data-testid="start-time-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <ScrollArea className="h-48">
                        {generateTimeOptions().map(time => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Hora fin *</Label>
                  <Select 
                    value={formData.end_time} 
                    onValueChange={(value) => setFormData({ ...formData, end_time: value })}
                  >
                    <SelectTrigger data-testid="end-time-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <ScrollArea className="h-48">
                        {generateTimeOptions().map(time => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Visit type: Con cita (booked) vs Walk-in */}
              <div className="space-y-2">
                <Label>Tipo de visita</Label>
                <div className="inline-flex rounded-md border border-slate-200 bg-slate-100 p-1" data-testid="visit-type-toggle">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, visit_type: 'booked' })}
                    className={cn(
                      'px-3 py-1.5 text-sm font-medium rounded transition-colors',
                      (formData.visit_type || 'booked') === 'booked'
                        ? 'bg-emerald-600 text-white shadow'
                        : 'text-slate-600 hover:bg-white'
                    )}
                    data-testid="visit-type-booked"
                  >
                    Con cita
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, visit_type: 'walk_in' })}
                    className={cn(
                      'px-3 py-1.5 text-sm font-medium rounded transition-colors',
                      formData.visit_type === 'walk_in'
                        ? 'bg-amber-600 text-white shadow'
                        : 'text-slate-600 hover:bg-white'
                    )}
                    data-testid="visit-type-walkin"
                  >
                    Walk-in
                  </button>
                </div>
              </div>

              {/* Status & Color */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select
                    value={
                      formData.status === 'custom' && formData.custom_status_label
                        ? `custom:${formData.custom_status_label}`
                        : formData.status
                    }
                    onValueChange={(value) => {
                      if (value === '__new__') {
                        setNewStatusOpen(true);
                        return;
                      }
                      if (value.startsWith('custom:')) {
                        const label = value.slice('custom:'.length);
                        const cs = customStatuses.find(s => s.label === label);
                        setFormData({
                          ...formData,
                          status: 'custom',
                          custom_status_label: label,
                          custom_color: cs?.color || formData.custom_color,
                        });
                      } else {
                        // Built-in status: clear any custom label/color
                        setFormData({
                          ...formData,
                          status: value,
                          custom_status_label: '',
                          // keep manual custom_color if user set one separately
                        });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key} data-testid={`status-option-${key}`}>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded border" 
                              style={{ backgroundColor: config.bgColor, borderColor: config.borderColor }}
                            />
                            {config.label}
                          </div>
                        </SelectItem>
                      ))}
                      {customStatuses.length > 0 && (
                        <div className="my-1 border-t border-slate-100" />
                      )}
                      {customStatuses.map((cs) => (
                        <div
                          key={cs.id}
                          className="relative group"
                        >
                          <SelectItem value={`custom:${cs.label}`} data-testid={`status-option-custom-${cs.label}`}>
                            <div className="flex items-center gap-2 pr-6">
                              <div
                                className="w-3 h-3 rounded border"
                                style={{ backgroundColor: cs.color, borderColor: cs.color }}
                              />
                              {cs.label}
                            </div>
                          </SelectItem>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeCustomStatus(cs.id);
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100"
                            title="Eliminar estado personalizado"
                            data-testid={`status-remove-${cs.id}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <div className="my-1 border-t border-slate-100" />
                      <SelectItem value="__new__" data-testid="status-create-new">
                        <div className="flex items-center gap-2 text-emerald-700 font-medium">
                          <Plus className="w-3.5 h-3.5" />
                          Crear nuevo estado…
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Color personalizado</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start gap-2">
                        {formData.custom_color ? (
                          <>
                            <div 
                              className="w-4 h-4 rounded border"
                              style={{ backgroundColor: formData.custom_color }}
                            />
                            {formData.custom_color}
                          </>
                        ) : (
                          <>
                            <Palette className="w-4 h-4" />
                            Sin color
                          </>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-3">
                      <div className="grid grid-cols-6 gap-2">
                        {PRESET_COLORS.map(color => (
                          <button
                            key={color}
                            className={cn(
                              "w-6 h-6 rounded border-2 hover:scale-110 transition-transform",
                              formData.custom_color === color ? "border-slate-800" : "border-slate-200"
                            )}
                            style={{ backgroundColor: color }}
                            onClick={() => setFormData({ ...formData, custom_color: color })}
                          />
                        ))}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full mt-2"
                        onClick={() => setFormData({ ...formData, custom_color: '' })}
                      >
                        Quitar color
                      </Button>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Etiquetas
                </Label>
                <div className="flex flex-wrap gap-2">
                  {COMMON_TAGS.map(tag => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={cn(
                        "px-2 py-1 text-xs rounded-full border transition-colors",
                        formData.tags.includes(tag) 
                          ? "bg-primary text-primary-foreground border-primary" 
                          : "bg-muted border-border hover:bg-muted/80"
                      )}
                      data-testid={`tag-preset-${tag}`}
                    >
                      {tag}
                    </button>
                  ))}
                  {customTags.map(tag => (
                    <span
                      key={`custom-${tag}`}
                      className={cn(
                        "inline-flex items-center gap-1 pl-2 pr-1 py-1 text-xs rounded-full border transition-colors",
                        formData.tags.includes(tag)
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => toggleTag(tag)}
                        className="cursor-pointer"
                        data-testid={`tag-custom-${tag}`}
                      >
                        {tag}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeCustomTag(tag)}
                        className={cn(
                          "ml-0.5 rounded-full p-0.5 transition-colors",
                          formData.tags.includes(tag)
                            ? "hover:bg-emerald-700"
                            : "hover:bg-emerald-200"
                        )}
                        title="Eliminar etiqueta personalizada"
                        data-testid={`tag-custom-remove-${tag}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Input
                    value={newTagInput}
                    onChange={(e) => setNewTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomTag();
                      }
                    }}
                    placeholder="Nueva etiqueta personalizada…"
                    maxLength={24}
                    className="h-8 text-sm"
                    data-testid="new-tag-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addCustomTag}
                    disabled={!newTagInput.trim()}
                    data-testid="add-tag-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    Agregar
                  </Button>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Notas
                </Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Observaciones, instrucciones especiales..."
                  rows={3}
                  data-testid="notes-input"
                />
              </div>

              {/* Options checkboxes */}
              <div className="space-y-3 pt-2 border-t border-border">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.allow_overlap}
                    onChange={(e) => setFormData({ ...formData, allow_overlap: e.target.checked })}
                    className="w-4 h-4 rounded border-amber-400 accent-amber-500"
                    data-testid="allow-overlap-checkbox"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-amber-700 group-hover:text-amber-800">
                      Permitir empalme de horarios
                    </span>
                    <p className="text-xs text-slate-500">
                      Marcar si deseas crear la cita aunque se empalme con otra
                    </p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.is_admin}
                    onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked, status: e.target.checked ? 'admin' : formData.status })}
                    className="w-4 h-4 rounded border-input"
                    data-testid="is-admin-checkbox"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium">Cita administrativa</span>
                    <p className="text-xs text-slate-500">
                      Marcar para bloquear tiempo (reunión, capacitación, etc.)
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>
            </TabsContent>

            {selectedAppointment && (
              <TabsContent value="historial" className="mt-3 flex-1 overflow-hidden">
                <div className="max-h-[calc(90vh-220px)] overflow-y-auto overscroll-contain scrollbar-visible">
                  <div className="px-6 py-4" data-testid="appointment-history-list">
                    {historyLoading ? (
                      <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                    ) : appointmentHistory.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-8">Sin eventos registrados.</p>
                    ) : (
                      <div className="border border-slate-200 rounded-md overflow-hidden">
                        <Table>
                          <TableHeader className="bg-slate-50">
                            <TableRow>
                              <TableHead className="text-xs">Acción</TableHead>
                              <TableHead className="text-xs">Fecha</TableHead>
                              <TableHead className="text-xs">Hora</TableHead>
                              <TableHead className="text-xs">Usuario</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {appointmentHistory.map((h, idx) => {
                              const actionCfg = {
                                created: { label: 'Crear', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
                                moved: { label: 'Mover', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
                                resized: { label: 'Cambiar tamaño', cls: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
                                status_changed: { label: 'Cambiar estado', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
                                edited: { label: 'Editar', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
                                updated: { label: 'Editar', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
                                cancelled: { label: 'Cancelar', cls: 'bg-red-100 text-red-700 border-red-200' },
                                deleted: { label: 'Eliminar', cls: 'bg-slate-200 text-slate-700 border-slate-300' },
                              }[h.action] || { label: h.action, cls: 'bg-slate-100 text-slate-700' };
                              const roleLabel = {
                                master_admin: 'Administrador maestro',
                                admin: 'Administrador',
                                barber: 'Barbero',
                                stylist: 'Estilista',
                                client: 'Cliente',
                                receptionist: 'Recepcionista',
                              }[h.actor_role] || h.actor_role;
                              const isClientPublic = h.actor_type === 'client' || h.actor_role === 'client';
                              const dt = new Date(h.at);
                              const fecha = dt.toLocaleDateString('es-MX', {
                                day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Mexico_City',
                              });
                              const hora = dt.toLocaleTimeString('es-MX', {
                                hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'America/Mexico_City',
                              });
                              return (
                                <TableRow key={h.id || idx}>
                                  <TableCell>
                                    <Badge variant="outline" className={cn('text-xs border', actionCfg.cls)}>
                                      {actionCfg.label}
                                      {h._legacy && <span className="ml-1 text-[9px] opacity-60">LEGACY</span>}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-xs text-slate-600 font-mono whitespace-nowrap">{fecha}</TableCell>
                                  <TableCell className="text-xs text-slate-600 font-mono whitespace-nowrap">{hora}</TableCell>
                                  <TableCell>
                                    <div className="text-sm text-slate-700">
                                      {isClientPublic && <Globe className="inline w-3 h-3 mr-1 text-slate-400" />}
                                      {h.actor_name}
                                    </div>
                                    {roleLabel && (
                                      <div className="text-xs text-slate-400">
                                        {isClientPublic ? 'Cliente (web pública)' : roleLabel}
                                      </div>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            )}
          </Tabs>

          <DialogFooter className="p-6 pt-4 border-t border-border gap-2 shrink-0">
            {selectedAppointment && (
              <Button 
                variant="destructive" 
                onClick={handleDeleteAppointment}
                className="mr-auto"
                data-testid="delete-appointment-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </Button>
            )}
            <Button variant="outline" onClick={() => setAppointmentDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveAppointment} data-testid="save-appointment-btn">
              {selectedAppointment ? 'Actualizar' : 'Crear cita'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline "Nuevo cliente" dialog */}
      <Dialog open={newClientOpen} onOpenChange={setNewClientOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Nuevo cliente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Nombre *</Label>
                <Input
                  value={newClientData.first_name}
                  onChange={(e) => setNewClientData({ ...newClientData, first_name: e.target.value })}
                  placeholder="Juan"
                  autoFocus
                  data-testid="agenda-new-client-first-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Apellido *</Label>
                <Input
                  value={newClientData.last_name}
                  onChange={(e) => setNewClientData({ ...newClientData, last_name: e.target.value })}
                  placeholder="Pérez"
                  data-testid="agenda-new-client-last-name"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Teléfono <span className="text-xs text-slate-400">(opcional)</span></Label>
              <Input
                value={newClientData.phone}
                onChange={(e) => setNewClientData({ ...newClientData, phone: e.target.value })}
                placeholder="555-0123"
                data-testid="agenda-new-client-phone"
              />
            </div>
            <p className="text-xs text-slate-500">
              Solo nombre y apellido son obligatorios. Podrás completar el resto desde Clientes.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setNewClientOpen(false)}
              disabled={creatingClient}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateClientInline}
              disabled={creatingClient || !newClientData.first_name.trim() || !newClientData.last_name.trim()}
              className="bg-primary text-white hover:bg-primary/90"
              data-testid="agenda-new-client-save"
            >
              {creatingClient ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Crear y seleccionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline "Nuevo estado" dialog */}
      <Dialog open={newStatusOpen} onOpenChange={setNewStatusOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Nuevo estado personalizado
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nombre del estado *</Label>
              <Input
                value={newStatusData.label}
                onChange={(e) => setNewStatusData({ ...newStatusData, label: e.target.value })}
                placeholder='Ej: "En espera", "Pagada", "Recordar"'
                maxLength={24}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') addCustomStatus(); }}
                data-testid="new-status-label"
              />
              <p className="text-xs text-slate-400">{(newStatusData.label || '').length}/24</p>
            </div>
            <div className="space-y-1.5">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewStatusData({ ...newStatusData, color })}
                    className={cn(
                      'w-8 h-8 rounded-md border-2 transition-transform hover:scale-110',
                      newStatusData.color === color ? 'border-slate-800 ring-2 ring-slate-300' : 'border-slate-200'
                    )}
                    style={{ backgroundColor: color }}
                    data-testid={`new-status-color-${color}`}
                    title={color}
                  />
                ))}
                <input
                  type="color"
                  value={newStatusData.color}
                  onChange={(e) => setNewStatusData({ ...newStatusData, color: e.target.value })}
                  className="w-8 h-8 rounded-md border border-slate-200 cursor-pointer"
                  title="Color personalizado"
                />
              </div>
            </div>
            {/* Preview */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Vista previa</Label>
              <div
                className="rounded-md px-3 py-2 text-sm font-medium border-l-4"
                style={{
                  backgroundColor: newStatusData.color,
                  borderLeftColor: newStatusData.color,
                  color: '#1f2937',
                }}
              >
                {newStatusData.label || 'Nombre del estado'}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setNewStatusOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={addCustomStatus}
              disabled={!newStatusData.label.trim()}
              className="bg-primary text-white hover:bg-primary/90"
              data-testid="new-status-save"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear estado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


    </div>
  );
}
